import $ from 'jquery';
import _ from 'underscore';
import Backbone from "backbone";
import DeleteSelectedNodesOperation from "models/audioEditor/sequencerOperation/DeleteSelectedNodesOperation";
import StartWrapSequencerNode from "models/audioEditor/sequencerNodes/StartWrapSequencerNode";
import EndWrapSequencerNode from "models/audioEditor/sequencerNodes/EndWrapSequencerNode";
import DeletedSequencerNode from "models/audioEditor/sequencerNodes/DeletedSequencerNode";
import SequencerOperationUtils from "models/audioEditor/sequencerOperation/SequencerOperationUtils";
import Utils from "models/helper/Utils";
import RSVP from "rsvp";

function InterruptedBecauseOfWrapNodeOrphanError(message = "") {
    this.name = "InterruptedBecauseOfWrapNodeOrphanError";
    this.message = message;
}
InterruptedBecauseOfWrapNodeOrphanError.prototype = Error.prototype;


function SelectedNodeNeedToBeNextToEachotherError(message = "") {
    this.name = "SelectedNodeNeedToBeNextToEachotherError";
    this.message = message;
}
SelectedNodeNeedToBeNextToEachotherError.prototype = Error.prototype;

const DeleteBulkSelectedNodesOperation =  DeleteSelectedNodesOperation.extend({
    idAttribute: "id",


    constructor: function(attributes, options) {
        DeleteSelectedNodesOperation.apply(this, [attributes, options]);
    },

    execute : function(arrayOfNodePairToDelete){
        const {arrayOfNodeArrayToReplace, arrayOfDeleteNode} = this.deleteArrayOfNodePair(arrayOfNodePairToDelete);
        this.getSequencer().getHistoryManagement().addCreateNodeDeletedBulkLog(arrayOfNodeArrayToReplace, arrayOfDeleteNode);

        return {"arrayOfNodeArrayToReplace": arrayOfNodeArrayToReplace, "arrayOfDeleteNode": arrayOfDeleteNode };
    },

    deleteArrayOfNodePair : function(arrayOfNodePairToDelete){
        // IMPORTANT: Overlap detection has been moved to _detectOverlappingNodePairs() for potential future use
        // Previously this function blocked overlapping deletions, preventing forgiveness mode from being set
        // Now relying on forgiveness mode in ReplacementSequencerNode to handle overlapping restoration gracefully
        // See BULK_DELETION_FORGIVENESS_SYSTEM.md for the complete story
        
        const arrayOfNodeArrayToReplace = [];
        const arrayOfDeleteNode =[];
        const arrayOfDeletionSource =  [];
        arrayOfNodePairToDelete.forEach((function(nodePairArray){
            const startSequencerNodeToDelete = nodePairArray[0];
            const endSequencerNodeToDelete = nodePairArray[1];
            if(!startSequencerNodeToDelete.isAddedToSequencer() || !endSequencerNodeToDelete.isAddedToSequencer()){
                //The start sequencer node or the end sequencer node was delete so just ignore that segment
                return;
            }
            const deletionSource = nodePairArray.length > 2? nodePairArray[2]: null;
            if(Utils.getInstance().doesSelectedNodesContainOrphanWrapNode(startSequencerNodeToDelete, endSequencerNodeToDelete)){
                throw new DeleteSelectedNodesOperation.InterruptedBecauseOfWrapNodeOrphanError();
            }
            const nodeArrayToReplace = SequencerOperationUtils.getInstance().getNodeArray(startSequencerNodeToDelete, endSequencerNodeToDelete);
            
            // Check if any nodes in the current array are already deleted/disconnected
            const alreadyDeletedNodesInArray = nodeArrayToReplace.filter(node => !node.isAddedToSequencer());
            if(alreadyDeletedNodesInArray.length > 0) {
                console.error("DeleteBulkSelectedNodesOperation: Found already deleted nodes", {
                    alreadyDeletedNodeCids: alreadyDeletedNodesInArray.map(n => n.cid)
                });
                return; // Skip this operation to prevent conflicts
            }
            
            if(SequencerOperationUtils.getInstance().areNodeToReplaceConnectedToEachother(nodeArrayToReplace)){
                arrayOfNodeArrayToReplace.push(nodeArrayToReplace);
                arrayOfDeletionSource.push(deletionSource);
            }else{
                throw new SelectedNodeNeedToBeNextToEachotherError();
            }
        }).bind(this))
        arrayOfNodeArrayToReplace.forEach((function(nodeArrayToReplace, index){
            const deleteNode = new DeletedSequencerNode({"audioSegment": Utils.getInstance().getDeletedAudioSegment(), "sequencer": this.getSequencer()});
            const deletionSource = arrayOfDeletionSource[index];
            deleteNode.setDeletionSource(deletionSource);
            
            // CRITICAL: Enable forgiveness mode for bulk deletion operations
            // This allows overlapping node restoration during undo without "next node already set" errors
            // Required for "safe cut" algorithm that creates overlapping deletion groups
            // See BULK_DELETION_FORGIVENESS_SYSTEM.md for complete explanation
            deleteNode.setAllowSharedNodeRestore(true);
            
            this.replaceSequencerNodes(deleteNode, nodeArrayToReplace);
            arrayOfDeleteNode.push(deleteNode);
        }).bind(this))

        return {arrayOfNodeArrayToReplace, arrayOfDeleteNode}

    },




    undo : function(sequencerOperationDeleteNodesLog ){
        const arrayOfDeleteNode = sequencerOperationDeleteNodesLog.getArrayOfDeleteNode();
        arrayOfDeleteNode.forEach((function(deleteNode){
            this.revertReplacement(deleteNode);
        }).bind(this))
    },

    redo: function(sequencerOperationDeleteNodesLog){
        const arrayOfNodeArrayToReplace = sequencerOperationDeleteNodesLog.getArrayOfNodeArrayToDelete();
        const arrayOfDeleteNode = sequencerOperationDeleteNodesLog.getArrayOfDeleteNode();
        arrayOfDeleteNode.forEach((function(deleteNode, index){
            const nodeArrayToReplace = arrayOfNodeArrayToReplace[index];
            this.replaceSequencerNodes(deleteNode, nodeArrayToReplace);
        }).bind(this))
    },

    /**
     * Internal overlap detection function preserved for potential future use
     * 
     * This function was previously used to prevent overlapping bulk deletions,
     * but was moved to internal status when we implemented forgiveness mode.
     * 
     * Usage: Call this.detectOverlappingNodePairs(arrayOfNodePairToDelete) 
     * in deleteArrayOfNodePair() if strict overlap prevention is needed.
     * 
     * @param {Array} arrayOfNodePairToDelete - Array of node pairs to check for overlaps
     * @returns {boolean} true if no overlaps detected (throws error if overlaps found)
     * @throws {Error} If overlapping nodes are detected between deletion pairs
     */
    _detectOverlappingNodePairs: function(arrayOfNodePairToDelete) {
        const allNodeCidsAcrossAllPairs = new Set();
        const nodeCidToPairIndexMap = new Map();
        
        // First pass: detect overlaps and log detailed information
        arrayOfNodePairToDelete.forEach((function(nodePairArray, pairIndex){
            const startSequencerNodeToDelete = nodePairArray[0];
            const endSequencerNodeToDelete = nodePairArray[1];
            if(!startSequencerNodeToDelete.isAddedToSequencer() || !endSequencerNodeToDelete.isAddedToSequencer()){
                return;
            }
            
            const nodeArrayForThisPair = SequencerOperationUtils.getInstance().getNodeArray(startSequencerNodeToDelete, endSequencerNodeToDelete);
            const nodeCidsForThisPair = nodeArrayForThisPair.map(n => n.cid);
            
            // Check for overlaps with previously processed pairs
            const overlappingNodes = [];
            const conflictingPairs = new Set();
            
            nodeCidsForThisPair.forEach(nodeCid => {
                if(nodeCidToPairIndexMap.has(nodeCid)) {
                    overlappingNodes.push(nodeCid);
                    conflictingPairs.add(nodeCidToPairIndexMap.get(nodeCid));
                } else {
                    nodeCidToPairIndexMap.set(nodeCid, pairIndex);
                }
            });
            
            if(overlappingNodes.length > 0) {
                // Build detailed information about conflicting pairs
                const conflictDetails = Array.from(conflictingPairs).map(conflictPairIndex => {
                    const conflictPair = arrayOfNodePairToDelete[conflictPairIndex];
                    return {
                        pairIndex: conflictPairIndex,
                        startNodeCid: conflictPair[0].cid,
                        endNodeCid: conflictPair[1].cid,
                        startNodeContent: conflictPair[0].getContent ? conflictPair[0].getContent() : 'N/A',
                        endNodeContent: conflictPair[1].getContent ? conflictPair[1].getContent() : 'N/A'
                    };
                });
                
                const currentPairDetails = {
                    pairIndex: pairIndex,
                    startNodeCid: startSequencerNodeToDelete.cid,
                    endNodeCid: endSequencerNodeToDelete.cid,
                    startNodeContent: startSequencerNodeToDelete.getContent ? startSequencerNodeToDelete.getContent() : 'N/A',
                    endNodeContent: endSequencerNodeToDelete.getContent ? endSequencerNodeToDelete.getContent() : 'N/A'
                };
                
                console.error("DeleteBulkSelectedNodesOperation: Overlapping nodes detected between deletion pairs", {
                    overlappingNodeCids: overlappingNodes,
                    currentPair: currentPairDetails,
                    conflictingPairs: conflictDetails,
                    totalPairsBeingProcessed: arrayOfNodePairToDelete.length
                });
                throw new Error("Overlapping nodes detected between deletion pairs in the same bulk operation. This would cause node linking conflicts.");
            }
        }).bind(this));
        
        return true; // No overlaps detected
    },

});

DeleteBulkSelectedNodesOperation.InterruptedBecauseOfWrapNodeOrphanError = InterruptedBecauseOfWrapNodeOrphanError;
DeleteBulkSelectedNodesOperation.SelectedNodeNeedToBeNextToEachotherError = SelectedNodeNeedToBeNextToEachotherError;
export default DeleteBulkSelectedNodesOperation;
